import { useCallback, useEffect, useMemo, useState } from "react";
import { usePeopleList } from "../../room/usePeopleList";
import { useParticipantLayoutDialogMediaState } from "./useParticipantLayoutDialogMediaState";
import {
  PARTICIPANT_TILE_KIND,
  canToggleParticipantLayoutMutedState,
  createParticipantLayoutScreenShareParticipant,
  getParticipantLayoutInitials,
  getParticipantLayoutRoleLabel,
  isParticipantLayoutScreenShareId,
  sortParticipantLayoutParticipants
} from "./participant-layout-participants";

const AUTO_LAYOUT_MODE = "auto";
const GRID_LAYOUT_MODE = "grid";
const SPOTLIGHT_LAYOUT_MODE = "spotlight";
const AUTO_SHARE_PIN_SOURCE = "auto-share";
const USER_PIN_SOURCE = "user";
const PARTICIPANT_LAYOUT_SIGN_IN_MESSAGES = {
  muteUser: {
    id: "sign-in-modal.signin-message.mute-user",
    defaultMessage: "You'll need to sign in to mute other users."
  }
};

function isSupportedLayoutMode(mode) {
  return mode === AUTO_LAYOUT_MODE || mode === GRID_LAYOUT_MODE || mode === SPOTLIGHT_LAYOUT_MODE;
}

function getParticipantDisplayName(person) {
  return person.profile?.displayName || person.displayName || "Guest";
}

function isEnteredParticipantLayoutPerson(person) {
  return person?.presence === "room";
}

function getParticipantMutedState(person, localMicEnabled) {
  if (person.isMe && typeof localMicEnabled === "boolean") {
    return !localMicEnabled;
  }

  return Boolean(person.micPresence?.muted);
}

function mapPeopleToParticipantLayoutParticipants(
  people,
  pinnedTileId,
  roomSpotlightTileId,
  canManageRoomSpotlight,
  roomSpotlightActive,
  canMuteUsers,
  localMicEnabled,
  cameraMediaStreamsByOwnerId
) {
  return people.map(person => {
    const displayName = getParticipantDisplayName(person);
    const isMuted = getParticipantMutedState(person, localMicEnabled);
    const mediaStream = cameraMediaStreamsByOwnerId?.[person.id] || null;
    const participant = {
      id: person.id,
      kind: PARTICIPANT_TILE_KIND,
      displayName,
      initials: getParticipantLayoutInitials(displayName),
      isMuted,
      isSpeaking: false,
      isPinned: person.id === pinnedTileId,
      isRoomSpotlighted: person.id === roomSpotlightTileId,
      canToggleRoomSpotlight: Boolean(canManageRoomSpotlight),
      roomSpotlightActive: Boolean(roomSpotlightActive),
      isHandRaised: Boolean(person.hand_raised),
      isSelf: Boolean(person.isMe),
      role: getParticipantLayoutRoleLabel(person.roles),
      ...(mediaStream ? { mediaStream } : {})
    };

    return {
      ...participant,
      canToggleMuted:
        canToggleParticipantLayoutMutedState({
          canMuteUsers,
          participant
        }) &&
        person.presence === "room" &&
        Boolean(person.permissions)
    };
  });
}

function getScreenSharePinnedTileId({ effectivePinnedTileId, pinSource, participantCount, screenShareCount } = {}) {
  if (pinSource === AUTO_SHARE_PIN_SOURCE && participantCount === 1 && screenShareCount === 1) {
    return null;
  }

  return effectivePinnedTileId;
}

export function useParticipantLayoutRoomState({
  enabled = true,
  presences,
  mySessionId,
  hubChannel,
  performConditionalSignIn,
  roomSpotlightTileId,
  onRoomSpotlightChange
}) {
  const [mode, setModeState] = useState(AUTO_LAYOUT_MODE);
  const people = usePeopleList(presences, mySessionId);
  const enteredPeople = useMemo(() => people.filter(isEnteredParticipantLayoutPerson), [people]);
  const {
    cameraMediaStreamsByOwnerId,
    localMicEnabled,
    screenShareActive,
    screenShares = []
  } = useParticipantLayoutDialogMediaState({
    enabled,
    mySessionId
  });
  const activeScreenShareIds = useMemo(() => screenShares.map(screenShare => screenShare.id), [screenShares]);
  const primaryScreenShareId = activeScreenShareIds[0] || null;
  const enteredParticipantIds = useMemo(() => enteredPeople.map(person => person.id), [enteredPeople]);
  const participantTileIds = useMemo(
    () => new Set([...enteredParticipantIds, ...activeScreenShareIds]),
    [activeScreenShareIds, enteredParticipantIds]
  );
  const effectiveRoomSpotlightTileId =
    roomSpotlightTileId && participantTileIds.has(roomSpotlightTileId) ? roomSpotlightTileId : null;
  const [pinnedTileId, setPinnedTileId] = useState(null);
  const [autoSharePinSuppressed, setAutoSharePinSuppressed] = useState(false);
  const roomSpotlightActive = Boolean(effectiveRoomSpotlightTileId);
  const effectivePinnedTileId =
    effectiveRoomSpotlightTileId || pinnedTileId || (!autoSharePinSuppressed ? primaryScreenShareId : null);
  const pinSource = effectiveRoomSpotlightTileId
    ? "room-spotlight"
    : pinnedTileId
    ? USER_PIN_SOURCE
    : effectivePinnedTileId
    ? AUTO_SHARE_PIN_SOURCE
    : null;
  const canMuteUsers = hubChannel.canOrWillIfCreator
    ? hubChannel.canOrWillIfCreator("mute_users")
    : hubChannel.can?.("mute_users");
  const canManageRoomSpotlight = Boolean(hubChannel.can?.("update_hub") && onRoomSpotlightChange);
  const screenSharePinnedTileId = getScreenSharePinnedTileId({
    effectivePinnedTileId,
    pinSource,
    participantCount: enteredPeople.length,
    screenShareCount: screenShares.length
  });

  const setMode = useCallback(nextMode => {
    setModeState(currentMode => (isSupportedLayoutMode(nextMode) ? nextMode : currentMode));
  }, []);

  const reset = useCallback(() => {
    setModeState(AUTO_LAYOUT_MODE);
    setPinnedTileId(null);
    setAutoSharePinSuppressed(false);
  }, []);

  const participants = useMemo(() => {
    const layoutParticipants = mapPeopleToParticipantLayoutParticipants(
      enteredPeople,
      effectivePinnedTileId,
      effectiveRoomSpotlightTileId,
      canManageRoomSpotlight,
      roomSpotlightActive,
      canMuteUsers,
      localMicEnabled,
      cameraMediaStreamsByOwnerId
    );

    if (screenShares.length > 0) {
      screenShares.forEach(screenShare => {
        layoutParticipants.push(
          createParticipantLayoutScreenShareParticipant({
            participants: layoutParticipants,
            ownerId: screenShare.ownerId,
            screenShareId: screenShare.id,
            pinnedTileId: screenSharePinnedTileId,
            roomSpotlightTileId: effectiveRoomSpotlightTileId,
            canManageRoomSpotlight,
            roomSpotlightActive,
            mediaStream: screenShare.mediaStream
          })
        );
      });
    }

    return sortParticipantLayoutParticipants(layoutParticipants);
  }, [
    canMuteUsers,
    canManageRoomSpotlight,
    cameraMediaStreamsByOwnerId,
    effectivePinnedTileId,
    effectiveRoomSpotlightTileId,
    enteredPeople,
    localMicEnabled,
    roomSpotlightActive,
    screenSharePinnedTileId,
    screenShares
  ]);

  const toggleParticipantMuted = useCallback(
    participantId => {
      const participant = participants.find(currentParticipant => currentParticipant.id === participantId);

      if (!participant?.canToggleMuted) {
        return;
      }

      const muteParticipant = () => hubChannel.mute(participant.id);

      if (performConditionalSignIn) {
        performConditionalSignIn(
          () => hubChannel.can("mute_users"),
          muteParticipant,
          PARTICIPANT_LAYOUT_SIGN_IN_MESSAGES.muteUser
        );
        return;
      }

      if (hubChannel.can?.("mute_users")) {
        muteParticipant();
      }
    },
    [hubChannel, participants, performConditionalSignIn]
  );

  const toggleParticipantPinned = useCallback(
    participantId => {
      if (roomSpotlightActive) {
        return;
      }

      setPinnedTileId(currentPinnedTileId => {
        const isAutoPinnedScreenShare =
          !currentPinnedTileId &&
          participantId === effectivePinnedTileId &&
          isParticipantLayoutScreenShareId(participantId);
        const nextPinnedTileId =
          currentPinnedTileId === participantId || isAutoPinnedScreenShare ? null : participantId;

        setAutoSharePinSuppressed(!nextPinnedTileId && isParticipantLayoutScreenShareId(participantId));

        return nextPinnedTileId;
      });
    },
    [effectivePinnedTileId, roomSpotlightActive]
  );

  const toggleParticipantRoomSpotlight = useCallback(
    participantId => {
      if (!canManageRoomSpotlight) {
        return;
      }

      onRoomSpotlightChange(effectiveRoomSpotlightTileId === participantId ? null : participantId);
    },
    [canManageRoomSpotlight, effectiveRoomSpotlightTileId, onRoomSpotlightChange]
  );

  useEffect(() => {
    if (!pinnedTileId) {
      return;
    }

    const hasPinnedParticipant = enteredPeople.some(person => person.id === pinnedTileId);
    const hasPinnedScreenShare = activeScreenShareIds.includes(pinnedTileId);

    if (hasPinnedParticipant || hasPinnedScreenShare) {
      return;
    }

    setPinnedTileId(null);
  }, [activeScreenShareIds, enteredPeople, pinnedTileId]);

  useEffect(() => {
    if (!screenShareActive) {
      setAutoSharePinSuppressed(false);
      setPinnedTileId(currentPinnedTileId =>
        isParticipantLayoutScreenShareId(currentPinnedTileId) ? null : currentPinnedTileId
      );
    }
  }, [screenShareActive]);

  const debugControls = useMemo(
    () => ({
      reset,
      setMode
    }),
    [reset, setMode]
  );

  return {
    mode,
    participants,
    debugControls,
    toggleParticipantMuted,
    toggleParticipantPinned,
    toggleParticipantRoomSpotlight,
    effectiveRoomSpotlightTileId,
    pinSource
  };
}
